import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { api } from './api';

// Auto-login links in Outlevo's emails (src/auth/email-links.ts): a link to a page carries a single-use login token in
// its fragment, e.g. /billing#olv_login=<token>&to=buy-credits. The fragment never reaches a server; it is read and
// removed from the address bar before React Router, the journey tracker or anything else sees it, and the page's own
// anchor (#buy-credits) is put back. With nobody signed in, the token is traded for a Supabase magic-link sign-in.
// Someone already signed in keeps their session (and its second factor); the token is left unused. A used, expired or
// refused token changes nothing: the page asks for sign-in as before and then continues to it.

const FRAGMENT = /^#olv_login=([A-Za-z0-9_-]{43})(?:&to=([A-Za-z0-9_%-]*))?$/;
let pending: string | null = null;

/** Reads and strips the token from the address (call once, before the app renders). */
export function takeLoginFragment(loc: Pick<Location, 'hash' | 'pathname' | 'search'> = location, hist: Pick<History, 'replaceState' | 'state'> = history): void {
  const m = FRAGMENT.exec(loc.hash);
  if (!m) return;
  pending = m[1]!;
  let to = '';
  try { to = decodeURIComponent(m[2] ?? ''); } catch { to = ''; }
  hist.replaceState(hist.state, '', `${loc.pathname}${loc.search}${/^[A-Za-z0-9_-]+$/.test(to) ? `#${to}` : ''}`);
}

/** The token taken from the address, once. */
export function pendingLoginToken(): string | null {
  const t = pending;
  pending = null;
  return t;
}

/** Trades the token for a session: the API checks and burns it and mints a one-time code, which Supabase verifies. */
export async function redeemLoginToken(supabase: SupabaseClient, token: string): Promise<Session | null> {
  try {
    const r = await api<{ email: string; code: string }>('/public/auth/email-link', { method: 'POST', body: { token } });
    const v = await supabase.auth.verifyOtp({ email: r.email, token: r.code, type: 'email' });
    return v.data.session ?? null;
  } catch {
    refused = true;
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('olv-login-link-refused'));
    return null; // used, expired or refused: the normal sign-in takes over, and says why (refusedLoginLink)
  }
}

let refused = false;
/** Whether a sign-in link from an email was just refused (used already or expired): the sign-in page says so once. */
export function refusedLoginLink(): boolean { const r = refused; refused = false; return r; }
